import React, { useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { FAQSection } from '@/components/sections/FAQSection';
import { ClaimForm } from '@/forms/ClaimForm';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, AlertCircle, FileText, Scale, CheckCircle, ArrowRight, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PCPCarFinanceClaimsPage = () => {
  // Handle scroll behavior on page load
  useEffect(() => {
    if (window.location.hash) {
      // If there's a hash, scroll to that element after a short delay
      setTimeout(() => {
        const element = document.getElementById(window.location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const pcpQuestions = [
    {
      id: 'took_out_pcp',
      question: 'Did you take out PCP or HP car finance?',
      options: [
        { label: 'Yes, I had PCP finance', value: 'pcp' },
        { label: 'Yes, I had HP finance', value: 'hp' },
        { label: 'Yes, I had both PCP and HP at different times', value: 'both' },
        { label: 'I\'m not sure what type of finance I had', value: 'unsure' }
      ]
    },
    {
      id: 'commission_disclosed',
      question: 'Were you told that the dealer received commission from the lender?',
      options: [
        { label: 'Yes, commission was disclosed to me', value: 'yes' },
        { label: 'No, I was not told about any commission', value: 'no' },
        { label: 'I don\'t remember being told', value: 'unsure' }
      ]
    },
    {
      id: 'finance_date',
      question: 'When did you take out the car finance?',
      options: [
        { label: 'Between April 2007 and January 2021', value: 'eligible_period' },
        { label: 'After January 2021', value: 'after_ban' },
        { label: 'Before April 2007', value: 'before_period' },
        { label: 'I\'m not sure of the exact date', value: 'unsure' }
      ]
    },
    {
      id: 'interest_rate_explained',
      question: 'Was the interest rate on your finance fully explained to you?',
      options: [
        { label: 'Yes, it was clearly explained', value: 'yes' },
        { label: 'No, I didn\'t fully understand the interest rate', value: 'no' },
        { label: 'I\'m not sure', value: 'unsure' }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-24 lg:py-32 bg-primary overflow-hidden">
          <div className="absolute inset-0 hero-pattern opacity-20" />
          <div className="section-container relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">
                  SRA Regulated Law Firm
                </span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight mb-6">
                PCP Car Finance Claims
              </h1>
              <p className="text-lg sm:text-xl text-primary-foreground/80 leading-relaxed mb-8">
                Blue Lion Law supports clients in assessing potential mis-selling of PCP and HP 
                car finance, particularly where discretionary commission was not disclosed.
              </p>
              <Button variant="gold" size="xl" asChild>
                <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                  Find Your Agreements
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* What is PCP Mis-selling */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
                  Understanding PCP Mis-selling
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                  What is Mis-sold Car Finance?
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  PCP (Personal Contract Purchase) and HP (Hire Purchase) car finance agreements may have 
                  been mis-sold if you were not told about commission arrangements between the dealer and lender.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Under Discretionary Commission Arrangements (DCAs), dealers could increase the interest 
                  rate on your finance to earn more commission – without telling you. The FCA banned DCAs 
                  in January 2021 and is now investigating affected consumers.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  The FCA has estimated that average compensation could be around <strong>£830 per agreement</strong>, 
                  though individual amounts vary based on circumstances.
                </p>
                <div className="flex flex-wrap gap-3 mt-6">
                  <a href="/pcp/what-is-pcp-car-finance" className="text-accent hover:underline text-sm">
                    Learn more about PCP finance →
                  </a>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=600&h=400&fit=crop"
                  alt="Car dealership finance discussion"
                  className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FCA Action Section */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
                Regulatory Context
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                FCA Motor Finance Review
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                The Financial Conduct Authority (FCA) has been investigating motor finance practices 
                and has confirmed that consumers affected by DCAs may be entitled to compensation.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <AlertCircle className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">
                    £7.5bn Potential Redress
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    The FCA estimates the total cost of redress to lenders could be £7.5 billion 
                    for affected car finance agreements.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mb-4">
                    <Car className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">
                    DCAs Banned in 2021
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    The FCA banned Discretionary Commission Arrangements in January 2021. 
                    Agreements from April 2007 to October 2024 may be affected.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <Scale className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-heading font-bold text-foreground mb-2">
                    Consumer Redress Scheme
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    The FCA has introduced a consumer redress scheme to ensure affected 
                    consumers can seek compensation efficiently.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 text-center">
              <a href="/pcp/fca-motor-finance-compensation-scheme" className="text-accent hover:underline text-sm">
                Read more about the FCA compensation scheme →
              </a>
            </div>
          </div>
        </section>

        {/* Claim Form Section */}
        <section id="claim-form" className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
                Start Your Assessment
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Find Your Agreements
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Answer a few questions to help us understand your situation. This is not a guarantee 
                of a claim – eligibility depends on individual circumstances.
              </p>
            </div>

            <ClaimForm claimType="pcp" questions={pcpQuestions} />
          </div>
        </section>

        {/* Why Blue Lion Law */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Why Choose Blue Lion Law?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Shield, title: 'SRA Regulated', desc: 'Authorised and regulated by the Solicitors Regulation Authority' },
                { icon: Scale, title: 'Legal Expertise', desc: 'Professional legal assessment of your individual circumstances' },
                { icon: FileText, title: 'No Win, No Fee', desc: 'We operate on a no win, no fee basis for eligible claims' },
                { icon: CheckCircle, title: 'Case by Case', desc: 'Every case is assessed on its own merits' }
              ].map((item, idx) => (
                <Card key={idx} className="border-border text-center">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-6 h-6 text-accent" />
                    </div>
                    <h3 className="font-heading font-bold text-foreground mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Related Content */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <h2 className="font-heading text-2xl font-bold text-foreground mb-8 text-center">
              Learn More About PCP Claims
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <a href="/pcp/what-is-pcp-car-finance" className="p-6 rounded-xl border border-border hover:border-accent/50 transition-colors">
                <h3 className="font-heading font-semibold text-foreground mb-2">What is PCP Car Finance?</h3>
                <p className="text-sm text-muted-foreground">Understanding how PCP finance works and its key features.</p>
              </a>
              <a href="/pcp/how-pcp-is-mis-sold" className="p-6 rounded-xl border border-border hover:border-accent/50 transition-colors">
                <h3 className="font-heading font-semibold text-foreground mb-2">How PCP is Mis-sold</h3>
                <p className="text-sm text-muted-foreground">Learn about discretionary commission and how it affected consumers.</p>
              </a>
              <a href="/gap-insurance-claims" className="p-6 rounded-xl border border-border hover:border-accent/50 transition-colors">
                <h3 className="font-heading font-semibold text-foreground mb-2">GAP Insurance Claims</h3>
                <p className="text-sm text-muted-foreground">GAP insurance sold with your PCP may also have been mis-sold.</p>
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
};

export default PCPCarFinanceClaimsPage;
